import React, {
  MouseEventHandler,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import { Redirect, useHistory } from 'react-router-dom';
import axios from 'axios';
import { addWeeks } from 'date-fns';
import {
  SupraSystemSubscriptionStatus,
  supraSubscriptionTypeToLabelMap,
  SupraSystemSubscriptionType,
} from '@bb/domain/dist/types';
import {
  Button,
  EnhancedFilterDropdown,
  ExtraMenu,
  Icon,
  Menu,
  MenuItem,
  message,
  Modal,
  Space,
  TableProps,
  theme,
  Tooltip,
} from 'components';

import type {
  EnhancedAnsweringServiceType,
  IUserSearchForMLSStaffQuery,
  IUserSearchForMLSStaffResponse,
} from '@/app-package';
import { UserRoles, UserStatusTypes, UserSubRoles } from '@bb/users';
import ErrorReporter from '@app/errorReporter';
import styled from 'styled-components';
import { addAgentToHistory } from 'api/RecentSearchHistoryController';
import { AuthContext, CrossOrgAnsweringContext } from 'contexts';
import {
  addDatacyToCell,
  addDatacyToTableHeader,
  capitalize,
  CHAT_SESSION_STORAGE_KEYS,
  featureEnabled,
  MLS_STAFF_USERS_STORAGE_KEYS,
  paramaterize,
} from 'utils';
import RoleTag from 'components/RoleTag';
import SubRoleTag from 'components/SubRoleTag';
import searchForMLSStaff from 'api/UserController/searchForMLSStaff';
import BulkActionMenu from 'components/BulkActionMenu';
import {
  createBulkSupraSystemSubscriptions,
  sendBulkSupraSystemSubscriptionInvitations,
  removeManualCoop,
  getCanCreateNonBillableKey,
} from 'api/SupraSystemSubscriptionController';
import { isUserFriendlyError } from 'api/SupraSystemSubscriptionController/sendSupraSystemSubscriptionInvitation';
import { getWithExpiry, setWithExpiry } from 'utils/localStorageHelper';
import {
  SupraCoopType,
  supraCoopTypeToLabelMap,
} from '@bb/domain/dist/types/SupraSystemSubscription';
import { Dropdown, MenuProps } from 'antd';
import { DownOutlined } from '@ant-design/icons';
import SendSupraInviteModal from 'modals/SendSupraInviteModal';
import { useSendSupraInvite } from 'hooks/useSendSupraInvite';
import SearchTable from '../SearchTable';
import { useSearchParams } from '../../AnsweringSearchPage/hooks';
import { UserStatusTag } from '../../../components';
import SupraTag from '../Tags/SupraTag';
import { useMLSStaffContext } from '../MLSStaffContext';
import { answeringServiceTypesFilters } from '../constants';
import AnsweringServiceTypeTag from '../Tags/AnsweringServiceTypeTag';
import { TableSortType } from '../types';
import { ActionText } from '../styles';
import UserTypeTag from '../Tags/UserTypeTag';
import AddUserModal from './AddUserModal';
import ManualCoopModal from './ManualCoopModal';
import SupraCoopTypeTag from '../Tags/CoopTypeTag';
import SubscribeUserModal from './SubscribeUserModal';
import { useActivationFeeContext } from '../ActivationFeeContext';
import { SUBSCRIPTION_UPDATED_EVENT } from '../../BillingInvoicesPage/components/BalanceCard';

type TableUserType = IUserSearchForMLSStaffResponse['users'][number];

const DEFAULT_MLS_AGENT_SEARCH_QUERY: any = { name: '', page: 0, size: 20 };

const handleEsConstraint = (count: number) => (count > 10000 ? 10000 : count);

const MLSStaffUsersTable: React.FC = () => {
  const history = useHistory();
  const [searchParam, setSearchParam] = useSearchParams<{
    search?: string;
    userStatusesFilter: (keyof typeof UserStatusTypes)[];
    userTypesFilter: UserRoles[];
    userSubRolesFilter: UserSubRoles[];
    answeringServiceTypesFilter: EnhancedAnsweringServiceType[];
    supraTypesFilter: SupraSystemSubscriptionType[];
    supraStatusesFilter: SupraSystemSubscriptionStatus[];
    userCoopTypeFilter: SupraCoopType[];
    sort: TableSortType;
    userRedirectId?: string;
  }>();
  const [searchResult, setSearchResult] =
    React.useState<IUserSearchForMLSStaffResponse>();
  const { answeringConfirm } = useContext(CrossOrgAnsweringContext);
  const {
    text: debouncedText,
    page,
    setPage,
    setCsvLink,
    setTotal,
    setInitialTotal,
    loading,
    setLoading,
    setSelectedUsers,
    selectedUsers,
    modalVisible,
    setModalVisible,
    manualCoopModalVisible,
    setManualCoopModalVisible,
    manualCoopSerial,
    setManualCoopSerial,
    manualCoopPin,
    setManualCoopPin,
  } = useMLSStaffContext();

  const { sendInvite, loading: inviteLoading } = useSendSupraInvite();

  const [showInviteModal, setShowInviteModal] = useState(false);
  const [selectedInviteUser, setSelectedInviteUser] = useState<{
    id: string;
    orgId: string;
    email?: string;
    phone?: string;
  } | null>(null);

  // Type
  const [supraTypesFilter, setSupraTypesFilter] = React.useState<
    SupraSystemSubscriptionType[]
  >(
    getWithExpiry(MLS_STAFF_USERS_STORAGE_KEYS.supraTypesFilter)?.split(',') ||
      searchParam.supraTypesFilter,
  );

  // Role
  const [userTypesFilter, setUserTypesFilter] = React.useState<UserRoles[]>(
    getWithExpiry(MLS_STAFF_USERS_STORAGE_KEYS.userTypesFilter)?.split(',') ||
      searchParam.userTypesFilter,
  );

  // Sub Role
  const [userSubRolesFilter, setUserSubRolesFilter] = React.useState<
    UserSubRoles[]
  >(
    getWithExpiry(MLS_STAFF_USERS_STORAGE_KEYS.userSubRolesFilter)?.split(
      ',',
    ) || searchParam.userSubRolesFilter,
  );

  const [userCoopTypeFilter, setUserCoopTypeFilter] = React.useState<
    SupraCoopType[]
  >(
    getWithExpiry(MLS_STAFF_USERS_STORAGE_KEYS.userCoopTypeFilter)?.split(
      ',',
    ) || searchParam.userCoopTypeFilter,
  );
  // Access Status
  const [supraStatusesFilter, setSupraStatusesFilter] = React.useState<
    SupraSystemSubscriptionStatus[]
  >(
    getWithExpiry(MLS_STAFF_USERS_STORAGE_KEYS.supraStatusesFilter)?.split(
      ',',
    ) || searchParam.supraStatusesFilter,
  );
  const [sort, setSort] = React.useState<TableSortType>({
    ...searchParam.sort,
  });
  // Member/MLS Status
  const [userStatusesFilter, setUserStatusesFilter] = React.useState<
    (keyof typeof UserStatusTypes)[]
  >(
    getWithExpiry(MLS_STAFF_USERS_STORAGE_KEYS.userStatusesFilter)?.split(
      ',',
    ) || searchParam.userStatusesFilter,
  );
  // Concierge
  const [answeringServiceTypesFilter, setAnsweringServiceTypesFilter] =
    React.useState<EnhancedAnsweringServiceType[]>(
      getWithExpiry(
        MLS_STAFF_USERS_STORAGE_KEYS.answeringServiceTypesFilter,
      )?.split(',') || searchParam.answeringServiceTypesFilter,
    );

  // Subscribe states
  const [subscribeModalVisible, setSubscribeModalVisible] = useState(false);
  const [subscribeModalUser, setSubscribeModalUser] =
    useState<null | TableUserType>(null);

  const [canCreateNonBillableKeys, setCanCreateNonBillableKeys] =
    useState(true);

  useEffect(() => {
    (async () => {
      if (
        subscribeModalUser &&
        featureEnabled('supraAddUserFeature') &&
        subscribeModalUser.supraSubscriptionStatus ===
          SupraSystemSubscriptionStatus.unsubscribed
      ) {
        const res = await getCanCreateNonBillableKey(subscribeModalUser.orgId);
        setCanCreateNonBillableKeys(res);
      } else {
        // Reset to default when modal closes or conditions not met
        setCanCreateNonBillableKeys(true);
      }
    })();
  }, [subscribeModalUser]);

  const { activationRate, hasValidFullContract } = useActivationFeeContext();

  const isRendered = React.useRef(false);
  const initialFetchCount = useRef<number | null>(null);

  const searchDeps: React.DependencyList = [
    debouncedText,
    userTypesFilter,
    userSubRolesFilter,
    userStatusesFilter,
    answeringServiceTypesFilter,
    supraTypesFilter,
    supraStatusesFilter,
    userCoopTypeFilter,
    sort?.field,
    sort?.direction,
  ];

  const MLS_AGENT_SEARCH_QUERY: IUserSearchForMLSStaffQuery = {
    name: debouncedText,
    types: userTypesFilter,
    subRoles: userSubRolesFilter,
    statuses: userStatusesFilter,
    answeringServiceTypes: answeringServiceTypesFilter,
    supraSubscriptionTypes: supraTypesFilter,
    supraSubscriptionStatuses: supraStatusesFilter,
    // @ts-expect-error: missing field
    coopTypes: userCoopTypeFilter,
    sortField: sort?.field ?? 'firstName',
    sortOrder: sort?.direction ?? 'asc',
    page,
    size: 20,
  };

  const { auth } = React.useContext(AuthContext);

  const features = auth?.org.features;

  const doSearch = async () => {
    setLoading(true);
    try {
      const _csvLink = axios.getUri({
        url: '/user/search/mls_staff',
        method: 'get',
        params: { ...MLS_AGENT_SEARCH_QUERY, exportCsv: 'true' },
      });
      setCsvLink(_csvLink);

      // Temp fix: If there are filters in MLS Agent table when page loads, call API twice, once to populate the table and get the count of agents with current filter
      // and a second time to get the count of total agents belonging to the MLS. TODO: Refactor BE function to fetch the total count of agents belonging to the MLS.

      if (!initialFetchCount.current && userTypesFilter?.length) {
        const [result, { count: initialCount }] = await Promise.all([
          searchForMLSStaff(MLS_AGENT_SEARCH_QUERY),
          searchForMLSStaff(DEFAULT_MLS_AGENT_SEARCH_QUERY),
        ]);
        initialFetchCount.current = handleEsConstraint(initialCount);
        setInitialTotal(handleEsConstraint(initialCount));
        setTotal(handleEsConstraint(result.count));
        setSearchResult(result);
      } else {
        const result = await searchForMLSStaff(MLS_AGENT_SEARCH_QUERY);
        if (!initialFetchCount.current) {
          // Since ES fetches only the first 10000 results, initialize counts in agent table maxed out at 10000
          initialFetchCount.current = handleEsConstraint(result.count);
          setInitialTotal(handleEsConstraint(result.count));
        }
        setTotal(handleEsConstraint(result.count));
        setSearchResult(result);
      }
    } catch (e) {
      message.error('Error fetching data');
      ErrorReporter.error('Error fetching data', e);
    }

    // Send a signal that the subscription may have been updated so other pages can dynamically re-render content
    document.dispatchEvent(new CustomEvent(SUBSCRIPTION_UPDATED_EVENT));
    setLoading(false);
    // Trigger dummy resize to trigger useTableResizer to fetch correct tableHeaderHeight after data loads
    window.dispatchEvent(new Event('resize'));
  };

  useEffect(() => {
    (async () => {
      await doSearch();
    })();
  }, [...searchDeps, page]);

  if (searchParam.userRedirectId) {
    const userId = searchParam.userRedirectId;
    setSearchParam({ ...searchParam, userRedirectId: undefined });
    return <Redirect to={`/mls/user/${userId}`} />;
  }

  const setInitialTableState = () => {
    if (!isRendered.current) {
      isRendered.current = true;
      return;
    }
    setPage(0);
    setSearchParam({
      search: debouncedText || undefined,
      userTypesFilter,
      userSubRolesFilter,
      userStatusesFilter,
      answeringServiceTypesFilter,
      supraTypesFilter,
      supraStatusesFilter,
      userCoopTypeFilter,
      sort,
    });
  };

  const columns: TableProps['columns'] = ([
    {
      width: 140,
      title: 'Name',
      dataIndex: 'firstName',
      key: 'name',
      sorter: true,
      sortOrder: sort?.field === 'firstName' ? `${sort?.direction}end` : null,
      showSorterTooltip: true,
      render: (_, user) => (
        <Space align="center" size="middle">
          {`${user.firstName} ${user.lastName}`}
        </Space>
      ),
      onCell: addDatacyToCell('agents-name'),
      onHeaderCell: addDatacyToTableHeader('agents-name'),
    },
    {
      width: 140,
      title: 'Member ID',
      dataIndex: 'mls_numbers',
      key: 'name',
      render: (mls_numbers: TableUserType['mls_numbers']) => {
        if (!mls_numbers?.length) return <></>;

        return (
          <Space align="center" size="middle">
            {mls_numbers?.length ? (
              <FlexCol>
                {mls_numbers.map(({ board, number }) => (
                  <div key={`${board}-${number}`}>
                    {board}: {number}
                  </div>
                ))}
              </FlexCol>
            ) : (
              <></>
            )}
          </Space>
        );
      },
      onCell: addDatacyToCell('agents-mls-numbers'),
      onHeaderCell: addDatacyToTableHeader('agents-mls-numbers'),
    },
    {
      width: 180,
      title: 'Brokerage',
      dataIndex: 'orgName',
      sorter: true,
      sortOrder: sort?.field === 'orgName' ? `${sort?.direction}end` : null,
      showSorterTooltip: true,
      render: (orgName: string) => (
        <Space align="center" size="middle">
          {orgName}
        </Space>
      ),
      onCell: addDatacyToCell('agents-brokerage'),
      onHeaderCell: addDatacyToTableHeader('agents-brokerage'),
    },
    {
      width: 180,
      title: 'OID',
      dataIndex: 'oid',
      render: (oid: string, user: TableUserType) => (
        <FlexCol style={{ color: '#262626', fontSize: '14px', fontFamily: 'Inter', fontStyle: 'normal' }}>
          {user.addressName && (
            <span>{user.addressName}</span>
          )}
          <span>OID: {oid}</span>
        </FlexCol>
      ),
      onCell: addDatacyToCell('agents-oid'),
      onHeaderCell: addDatacyToTableHeader('agents-oid'),
    },
    {
      width: 110,
      title: 'Role',
      dataIndex: 'role',
      filters: Object.keys(UserRoles).map(role => ({
        text: role,
        value: UserRoles[role as keyof typeof UserRoles],
      })),
      filterDropdown: props => (
        <EnhancedFilterDropdown {...props} okText="Apply" resetText="Reset" />
      ),
      filteredValue: userTypesFilter,
      render: role => (
        <Flex>
          <RoleTag role={role} />
        </Flex>
      ),
      onCell: addDatacyToCell('agents-role'),
      onHeaderCell: addDatacyToTableHeader('agents-role'),
    },
    {
      width: 150,
      title: 'Sub Role',
      dataIndex: 'subRole',
      filters: Object.keys(UserSubRoles).map(role => ({
        text: role,
        value: UserSubRoles[role as keyof typeof UserSubRoles],
      })),
      filterDropdown: props => (
        <EnhancedFilterDropdown {...props} okText="Apply" resetText="Reset" />
      ),
      filteredValue: userSubRolesFilter,
      render: (subRole, { supraSubscriptionStatus }: TableUserType) => {
        if (
          supraSubscriptionStatus !== SupraSystemSubscriptionStatus.unsubscribed
        ) {
          return (
            <Flex>
              <SubRoleTag subRole={subRole} />
            </Flex>
          );
        }
        return <></>;
      },
      onCell: addDatacyToCell('agents-sub-role'),
      onHeaderCell: addDatacyToTableHeader('agents-sub-role'),
    },
    {
      width: 130,
      title: 'Key Type',
      dataIndex: 'supraSubscriptionType',
      filters: Object.values(SupraSystemSubscriptionType).map(type => ({
        text: supraSubscriptionTypeToLabelMap[type],
        value: type,
      })),
      filterDropdown: props => (
        <EnhancedFilterDropdown {...props} okText="Apply" resetText="Reset" />
      ),
      filteredValue: supraTypesFilter,
      render: (
        supraSubscriptionType,
        { supraSubscriptionStatus }: TableUserType,
      ) => {
        if (
          supraSubscriptionStatus !== SupraSystemSubscriptionStatus.unsubscribed
        ) {
          return <UserTypeTag type={supraSubscriptionType} />;
        }
        return <></>;
      },
      onCell: addDatacyToCell('agents-type'),
      onHeaderCell: addDatacyToTableHeader('agents-type'),
    },
    {
      width: 110,
      title: 'Key Status',
      dataIndex: 'supraSubscriptionStatus',
      filters: [
        { text: 'Subscribed', value: 'subscribed' },
        { text: 'Unsubscribed', value: 'unsubscribed' },
        { text: 'Pending', value: 'pending' },
        {
          text: (
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              Suspended
              <Tooltip placement="right" title="Payment is required">
                <span>
                  <Icon type="antdInfoCircleOutlined" offset={[8, 1]} />
                </span>
              </Tooltip>
            </div>
          ),
          value: 'suspended',
        },
        {
          text: (
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              Deactivated
              <Tooltip
                placement="right"
                title="Payment is required and late fee applies"
              >
                <span>
                  <Icon type="antdInfoCircleOutlined" offset={[8, 1]} />
                </span>
              </Tooltip>
            </div>
          ),
          value: 'deactivated',
        },
      ],
      filterDropdown: props => (
        <EnhancedFilterDropdown {...props} okText="Apply" resetText="Reset" />
      ),
      filteredValue: supraStatusesFilter,
      render: supraSubscriptionStatus => (
        <SupraTag status={supraSubscriptionStatus} />
      ),
      onCell: addDatacyToCell('agents-access-status'),
      onHeaderCell: addDatacyToTableHeader('agents-access-status'),
    },
    featureEnabled('supraAutoCoopFeature') && {
      width: 140,
      title: 'Co-op Type',
      dataIndex: 'coopType',
      filters: Object.values(SupraCoopType).map(type => ({
        text: supraCoopTypeToLabelMap[type as SupraCoopType],
        value: SupraCoopType[type],
      })),
      filterDropdown: props => (
        <EnhancedFilterDropdown {...props} okText="Apply" resetText="Reset" />
      ),
      filteredValue: userCoopTypeFilter,
      render: (type: SupraCoopType) => (
        <Flex>
          <SupraCoopTypeTag coopType={type} />
        </Flex>
      ),
    },
    {
      width: 135,
      title: 'Member/MLS Status',
      dataIndex: 'status',
      filters: Object.keys(UserStatusTypes).map(status => ({
        text: capitalize(status),
        value: status,
      })),
      filterDropdown: props => (
        <EnhancedFilterDropdown {...props} okText="Apply" resetText="Reset" />
      ),
      filteredValue: userStatusesFilter,
      render: (_, user: TableUserType) => (
        <Flex>
          <UserStatusTag
            disabled={user.disabled}
            isDeleted={user.isDeleted}
            isUnverified={user.isUnverified}
          />
        </Flex>
      ),
      onCell: addDatacyToCell('agents-scheduling-status'),
      onHeaderCell: addDatacyToTableHeader('agents-scheduling-status'),
    },
    {
      width: 125,
      title: 'Concierge',
      dataIndex: 'answeringServiceType',
      filters: answeringServiceTypesFilters,
      filterDropdown: props => (
        <EnhancedFilterDropdown {...props} okText="Apply" resetText="Reset" />
      ),
      filteredValue: answeringServiceTypesFilter,
      render: answeringServiceType => (
        <AnsweringServiceTypeTag answeringServiceType={answeringServiceType} />
      ),

      onCell: addDatacyToCell('agents-oid'),
      onHeaderCell: addDatacyToTableHeader('agents-oid'),
    },
    {
      title: 'Actions',
      dataIndex: 'id',
      width: 140,
      render: (
        id: string,
        {
          answeringServiceType,
          isChatDisabled,
          disabled,
          orgId,
          email,
          supraSubscriptionStatus,
          canSubscribeToSupra,
          coopType,
          role,
        }: TableUserType,
      ) => {
        const isPending =
          supraSubscriptionStatus === SupraSystemSubscriptionStatus.pending;

        const isSubscribed =
          supraSubscriptionStatus === SupraSystemSubscriptionStatus.subscribed;

        // Subscribe action should be disabled when:
        // - User can't subscribe to Supra, OR
        // - User is already subscribed, OR
        // - (Keyholder billing is enabled AND no valid contract), OR
        // - (User is pending AND invite is loading)
        const isSubscribeActionDisabled =
          !canSubscribeToSupra ||
          isSubscribed ||
          (featureEnabled('keyholderBillingFeature') &&
            !hasValidFullContract) ||
          (isPending && inviteLoading);

        const menu = (
          <Menu>
            {coopType === SupraCoopType.PRIMARY && (
              <MenuItem key={'assume'}>
                <ActionText
                  disabled={disabled}
                  onClick={event => {
                    event.stopPropagation();
                    if (disabled) return;
                    answeringConfirm(
                      { type: 'agent', subscriptionTag: answeringServiceType },
                      () => {
                        CHAT_SESSION_STORAGE_KEYS.forEach(key =>
                          sessionStorage.removeItem(key),
                        );
                        const pathname = `/answering/assume_user/${id}?${paramaterize(
                          { redirectUrl: '/my_business' },
                        )}`;
                        window.location.href = pathname;
                      },
                    )();
                  }}
                  data-cy={`agent-assume-button-${id}`}
                >
                  Assume
                </ActionText>
              </MenuItem>
            )}
            {coopType === SupraCoopType.PRIMARY && (
              <MenuItem key={'chat'}>
                <ActionText
                  disabled={isChatDisabled}
                  onClick={event => {
                    event.stopPropagation();
                    if (isChatDisabled) return;
                    answeringConfirm(
                      { type: 'agent', subscriptionTag: answeringServiceType },
                      () => {
                        addAgentToHistory(id);
                        axios.post('/messaging/newConvo', { userIds: [id] });
                      },
                    )();
                  }}
                  data-cy={`agent-chat-button-${id}`}
                >
                  Chat
                </ActionText>
              </MenuItem>
            )}
            {coopType === SupraCoopType.PRIMARY && (
              <MenuItem key={'subscribe-resend'}>
                <ActionText
                  disabled={isSubscribeActionDisabled}
                  onClick={event => {
                    event.stopPropagation();

                    // Use the same condition for early return
                    if (isSubscribeActionDisabled) return;

                    answeringConfirm(
                      { type: 'agent', subscriptionTag: answeringServiceType },
                      async () => {
                        if (isPending) {
                          // Handle resend invite for pending users - show confirmation
                          const currentUser = searchResult?.users?.find(
                            u => u.id === id,
                          );
                          setSelectedInviteUser({
                            id,
                            orgId,
                            email: currentUser?.email,
                            phone: undefined,
                          });
                          setShowInviteModal(true);
                        } else {
                          // Handle new subscription
                          try {
                            setLoading(true);

                            if (
                              supraSubscriptionStatus ===
                                SupraSystemSubscriptionStatus.unsubscribed &&
                              featureEnabled('keyholderBillingFeature')
                            ) {
                              setSubscribeModalUser({
                                id,
                                orgId,
                                email,
                                supraSubscriptionStatus,
                                role,
                              } as TableUserType);
                              setSubscribeModalVisible(true);
                            }
                          } catch (e) {
                            const errorMsg = isUserFriendlyError(
                              e?.response?.data?.message,
                            )
                              ? e.response.data.message
                              : 'Something went wrong, please try again.';
                            message.error(errorMsg);
                          } finally {
                            setLoading(false);
                            await doSearch();
                          }
                        }
                      },
                    )();
                  }}
                  data-cy={`agent-${isPending ? 'resend-invite' : 'subscribe'}-button-${id}`}
                >
                  {isPending && inviteLoading
                    ? 'Sending...'
                    : isPending
                      ? 'Resend Invite'
                      : 'Subscribe'}
                </ActionText>
              </MenuItem>
            )}
            {featureEnabled('supraManualCoopFeature') &&
              coopType === SupraCoopType.MANUAL_COOP && (
                <MenuItem key={'remove-manual-coop'}>
                  <RedActionText
                    onClick={event => {
                      event.stopPropagation();
                      const currentUser = searchResult?.users?.find(
                        u => u.id === id,
                      );

                      Modal.confirm({
                        title: 'Are you sure?',
                        content:
                          'By removing this manual co-op key, the agent will no longer be able to access lockboxes in your organization.',
                        width: 600,
                        centered: true,
                        okText: 'Remove Co-op Key',
                        okType: 'danger',
                        cancelText: 'Back',
                        onOk: async () => {
                          if (currentUser) {
                            try {
                              setLoading(true);
                              await removeManualCoop({
                                userId: currentUser.id,
                                orgId: currentUser.orgId,
                              });
                              message.success('Manual co-op key removed');
                              await doSearch(); // Refresh the table
                            } catch (error) {
                              message.error(
                                'Failed to remove manual co-op key',
                              );
                            } finally {
                              setLoading(false);
                            }
                          }
                        },
                      });
                    }}
                    data-cy={`agent-remove-manual-coop-button-${id}`}
                  >
                    Remove Co-op Key
                  </RedActionText>
                </MenuItem>
              )}
          </Menu>
        );

        // Show actions for PRIMARY and MANUAL_COOP types only
        // AUTO_COOP users should have no menu options
        if (
          coopType &&
          coopType !== SupraCoopType.PRIMARY &&
          coopType !== SupraCoopType.MANUAL_COOP
        )
          return <></>;
        return <ExtraMenu dropdownRender={() => menu} />;
      },
      onCell: addDatacyToCell('agents-chat'),
      onHeaderCell: addDatacyToTableHeader('agents-chat'),
    },
  ] as TableProps['columns'])!.filter(Boolean);

  const ClearSelectionHandler = (event: React.MouseEvent<HTMLElement>) => {
    setSelectedUsers([]);
  };

  const handleInviteConfirm = async () => {
    if (selectedInviteUser) {
      await sendInvite(
        { userId: selectedInviteUser.id, orgId: selectedInviteUser.orgId },
        () => {
          doSearch(); // Refresh the table
          setShowInviteModal(false);
          setSelectedInviteUser(null);
        },
        () => {
          setShowInviteModal(false);
          setSelectedInviteUser(null);
        },
      );
    }
  };

  const handleInviteCancel = () => {
    setShowInviteModal(false);
    setSelectedInviteUser(null);
  };

  const SendInvite = async (legacyUsersOnly: boolean) => {
    setLoading(true);
    try {
      const users = searchResult?.users ?? [];
      const usersToInvite = users
        .filter(
          user =>
            selectedUsers.includes(user.id) &&
            user.supraSubscriptionStatus !==
              SupraSystemSubscriptionStatus.subscribed &&
            user.canSubscribeToSupra,
        )
        .map(user => ({
          userId: user.id,
          orgId: user.orgId,
          supraStatus: user.supraSubscriptionStatus,
        }));

      if (usersToInvite.length === 0) {
        message.info(
          'No valid users selected. No subscriptions will be created.',
        );
        setSelectedUsers([]);
        return;
      }

      // Create subscriptions for new users
      try {
        const newUsersToInvite = usersToInvite.filter(
          user =>
            user.supraStatus === SupraSystemSubscriptionStatus.unsubscribed,
        );

        if (newUsersToInvite.length > 0) {
          await createBulkSupraSystemSubscriptions({
            usersToSubscribe: newUsersToInvite,
            legacyUsersOnly,
          });
        }
      } catch (error) {
        message.error('Error creating Supra subscriptions');
        ErrorReporter.error('Error creating Supra subscriptions', error);
        return;
      }

      // Send invites
      try {
        const invitedUserIds = await sendBulkSupraSystemSubscriptionInvitations(
          { usersToInvite, legacyUsersOnly },
        );

        if (invitedUserIds.length === 0 && legacyUsersOnly) {
          message.warn('No legacy users were selected. No invites were sent.');
        } else if (
          invitedUserIds.length < usersToInvite.length &&
          legacyUsersOnly
        ) {
          message.warn(
            'Not all selected users are legacy users. Invites will be sent only to the selected legacy users.',
          );
        }

        if (invitedUserIds.length > 0) {
          const successMessage =
            usersToInvite.length < selectedUsers.length
              ? 'Subscription invitation/s sent, but some users could not be invited'
              : 'Subscription invitation/s sent.';
          message.success(successMessage);
        }
      } catch (error) {
        message.error('Error sending subscription invitations');
        ErrorReporter.error('Error sending subscription invitations', error);
        return;
      }

      // Refresh page after success
      await doSearch();
      setSelectedUsers([]);
    } catch (error) {
      message.error('Error processing Supra subscriptions');
      ErrorReporter.error('Error processing Supra subscriptions', error);
      return;
    } finally {
      setLoading(false);
    }
  };

  const onSendInvitation: MouseEventHandler = async event => {
    SendInvite(false);
  };

  const onSendLegacyInvitation: MouseEventHandler = async event => {
    SendInvite(true);
  };

  const updateLocalStorageFilters = (filters: todo) => {
    // Supra Subscription Type
    setWithExpiry(
      MLS_STAFF_USERS_STORAGE_KEYS.supraTypesFilter,
      filters.supraSubscriptionType?.join(','),
      addWeeks(new Date(), 1).getTime(),
    );
    // Role
    setWithExpiry(
      MLS_STAFF_USERS_STORAGE_KEYS.userTypesFilter,
      filters.role?.join(','),
      addWeeks(new Date(), 1).getTime(),
    );
    // Sub Role
    setWithExpiry(
      MLS_STAFF_USERS_STORAGE_KEYS.userSubRolesFilter,
      filters.subRole?.join(','),
      addWeeks(new Date(), 1).getTime(),
    );
    // Access Status
    setWithExpiry(
      MLS_STAFF_USERS_STORAGE_KEYS.supraStatusesFilter,
      filters.supraSubscriptionStatus?.join(','),
      addWeeks(new Date(), 1).getTime(),
    );
    // Coop Type
    setWithExpiry(
      MLS_STAFF_USERS_STORAGE_KEYS.userCoopTypeFilter,
      filters.coopType?.join(','),
      addWeeks(new Date(), 1).getTime(),
    );
    // Member/MLS Status
    setWithExpiry(
      MLS_STAFF_USERS_STORAGE_KEYS.userStatusesFilter,
      filters.status?.join(','),
      addWeeks(new Date(), 1).getTime(),
    );
    // Concierge
    setWithExpiry(
      MLS_STAFF_USERS_STORAGE_KEYS.answeringServiceTypesFilter,
      filters.answeringServiceType?.join(','),
      addWeeks(new Date(), 1).getTime(),
    );
  };

  const items: MenuProps['items'] = [
    { key: '1', label: <span onClick={onSendInvitation}>Everyone</span> },
    {
      key: '2',
      label: <span onClick={onSendLegacyInvitation}>Legacy users only</span>,
    },
  ];

  return (
    <>
      <SearchTable
        rowKey={record => record.id}
        columns={columns}
        dataSource={searchResult?.users ?? []}
        onChange={(_, filters, sorter) => {
          setUserStatusesFilter(
            filters.status as (keyof typeof UserStatusTypes)[],
          );
          setUserTypesFilter(filters.role as UserRoles[]);
          setUserSubRolesFilter(filters.subRole as UserSubRoles[]);
          setAnsweringServiceTypesFilter(
            filters.answeringServiceType as EnhancedAnsweringServiceType[],
          );
          setSupraTypesFilter(
            filters.supraSubscriptionType as SupraSystemSubscriptionType[],
          );
          setSupraStatusesFilter(
            filters.supraSubscriptionStatus as SupraSystemSubscriptionStatus[],
          );
          setUserCoopTypeFilter(filters.coopType as SupraCoopType[]);
          updateLocalStorageFilters(filters);
          if ((sorter as any).field && (sorter as any).order) {
            setSort({
              field: (sorter as any).field,
              direction: (sorter as any).order === 'descend' ? 'desc' : 'asc',
            });
          } else {
            setSort(null);
          }
        }}
        onRow={user => ({
          onClick: () => {
            history.push(`/mls/user/${user.id}`);
          },
          'data-cy': `user-row-${user.id}`,
        })}
        dataCy={'agents-table'}
        loading={loading}
        totalItems={searchResult?.count || 0}
        rowSelection={{
          selectedRowKeys: selectedUsers,
          type: 'checkbox',
          onChange: (selectedRowKeys, selectedRows) => {
            setSelectedUsers(selectedRowKeys as string[]);
          },
          getCheckboxProps: record => ({ id: record.id }),
        }}
      />
      {selectedUsers.length > 0 && (
        <BulkActionMenu
          NumSelected={selectedUsers.length}
          ClearSelectionHandler={ClearSelectionHandler}
        >
          {features.includes('MLS_STAFF_SUBSCRIBE_LEGACY') ? (
            <Dropdown menu={{ items }} placement="topRight">
              <Button>
                Subscribe
                <DownOutlined />
              </Button>
            </Dropdown>
          ) : (
            <Button onClick={onSendInvitation}>Subscribe</Button>
          )}
        </BulkActionMenu>
      )}
      <SendSupraInviteModal
        open={showInviteModal}
        loading={inviteLoading}
        userEmail={selectedInviteUser?.email}
        userPhone={selectedInviteUser?.phone}
        onConfirm={handleInviteConfirm}
        onCancel={handleInviteCancel}
      />
      {featureEnabled('supraAddUserFeature') &&
        features.includes('MLS_STAFF_ADD_USERS') && (
          <AddUserModal visible={modalVisible} setVisible={setModalVisible} />
        )}
      {featureEnabled('supraManualCoopFeature') && (
        <ManualCoopModal
          visible={manualCoopModalVisible}
          setVisible={setManualCoopModalVisible}
          manualCoopSerial={manualCoopSerial}
          setManualCoopSerial={setManualCoopSerial}
          manualCoopPin={manualCoopPin}
          setManualCoopPin={setManualCoopPin}
        />
      )}
      <SubscribeUserModal
        visible={subscribeModalVisible}
        onClose={() => setSubscribeModalVisible(false)}
        onSuccess={doSearch} // callback to re-fetch
        user={subscribeModalUser}
        setLoading={setLoading}
        loading={loading}
        canCreateNonBillableKeys={canCreateNonBillableKeys}
        {...(featureEnabled('keyholderBillingFeature') && { activationRate })}
      />
    </>
  );
};

export default MLSStaffUsersTable;

const Flex = styled.div`
  display: flex;
`;

const FlexCol = styled(Flex)`
  flex-direction: column;
`;

const RedActionText = styled(ActionText)`
  color: ${theme.getColor('danger')};
`;
